import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

interface EmptyStateProps {
  /** Lucide icon shown above the heading */
  icon: LucideIcon;
  /** Main heading text */
  title: string;
  /** Secondary description */
  description?: string;
  /** Optional action button / CTA rendered below the description */
  action?: React.ReactNode;
  className?: string;
}

/**
 * Centered empty-state placeholder.
 *
 * ```tsx
 * <EmptyState
 *   icon={Users}
 *   title="No users found"
 *   description="Try a different search term"
 *   action={<button>Add user</button>}
 * />
 * ```
 */
export function EmptyState({ icon: Icon, title, description, action, className }: EmptyStateProps) {
  return (
    <div className={cn('bg-card rounded-xl border py-12 text-center', className)}>
      <Icon className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
      <h3 className="mb-2 font-semibold">{title}</h3>
      {description && <p className="text-muted-foreground text-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}
